import React, { useEffect } from 'react';
import { clsx } from 'clsx';
import { useTranslation, Trans } from 'react-i18next';
import { useItem, useItemSendToWarehouse } from '@/hooks/item';
import { ItemReturnToWarehouseModal } from '@/types/modal';
import {
  ErrorModal,
  LoadingBodyModal,
  NoFooterModal,
  StandardModal,
  SuccessModal,
} from '@/components/molecules/common/Modal';
import styles from './Modal.module.scss';
import { Box } from '@mui/material';
import { environments } from '@/environment';
import { BootService } from '@/services/boot';

const View: React.FC<{ modal: ItemReturnToWarehouseModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const itemR = useItem(item.productId, false);
  const submitR = useItemSendToWarehouse(item.productId);

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
    }
  }, [submitR.kind]);

  if (submitR.kind === 'Error') {
    if (submitR.error.errorCode === '422' && submitR.error.kind === 'ServerError') {
      const zendeskId = submitR.error.data?.data?.returnTicketId;
      const status = submitR.error.data?.message;
      const path = environments[BootService.env].zendeskUrl;

      const body = (
        <Box whiteSpace={'pre-wrap'}>
          <Trans
            i18nKey={
              status === 'FAIL_ZENDESK_MERGED_AND_CLOSED'
                ? 'modal.unlock_denied.closed_body'
                : 'modal.unlock_denied.body'
            }
            values={{ ticketNumber: zendeskId }}
            components={{
              ticketNumber: (
                <a
                  href={`${path}/agent/tickets/${zendeskId}`}
                  target="_blank"
                  style={{ color: 'blue', display: 'inline' }}
                  rel="noreferrer"
                />
              ),
            }}
          />
        </Box>
      );

      return (
        <NoFooterModal
          title={t('modal.unlock_denied.title')}
          onClose={onClose}
          body={body}
          modalName="item_return_to_warehouse"
        />
      );
    }

    return (
      <ErrorModal
        title={t('modal.item_return_to_warehouse.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    return (
      <SuccessModal
        title={t('modal.item_return_to_warehouse.title')}
        onClose={onClose}
      />
    );
  }

  const body =
    submitR.kind === 'Loading' ? (
      <LoadingBodyModal height={40} />
    ) : (
      <div className={clsx([styles.text1rem, styles.paddingY1rem])}>
        <div dangerouslySetInnerHTML={{ __html: t('modal.item_return_to_warehouse.body', item) }} />
      </div>
    );

  return (
    <StandardModal
      title={t('modal.item_return_to_warehouse.title')}
      onClose={onClose}
      onSubmit={submitR.mutate}
      closeLabel={t('common.no').toUpperCase()}
      submitLabel={t('common.yes').toUpperCase()}
      submittable={submitR.kind !== 'Loading'}
      body={body}
      containerStyle={{ width: 500 }}
      modalName="item_return_to_warehouse"
    />
  );
};

export default View;
